import { Link } from "react-router-dom";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import AnimatedBackground from "@/components/site/AnimatedBackground";
import { useContent } from "@/context/ContentContext";
import { Check, ArrowUpRight } from "lucide-react";

export default function Pricing() {
  const { content } = useContent();
  const PRICING = content.pricing || [];
  return (
    <div data-testid="pricing-page" className="relative pt-32">
      <AnimatedBackground variant="warm" />
      <section className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <p className="font-mono text-sm uppercase tracking-[0.3em] text-brand-blue">Enrolment</p>
        <h1 className="mt-6 max-w-4xl font-display text-5xl font-medium leading-[0.95] tracking-tight text-white sm:text-7xl">
          <MaskedLines lines={["Simple tiers."]} />
          <MaskedLines lines={["No hidden gates."]} delay={0.2} lineClassName="text-brand-gradient" />
        </h1>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-xl text-lg font-light leading-relaxed text-slate-400">
            Pricing is tailored to your goals and cohort. Tell us where you are and we'll recommend the
            right path — every plan is quoted after a short conversation.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-16 lg:px-12">
        <div className="grid gap-px border border-white/10 bg-white/10 lg:grid-cols-3">
          {PRICING.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1} className="h-full">
              <div
                data-testid={`pricing-${p.name.toLowerCase()}`}
                className={`relative flex h-full flex-col overflow-hidden p-10 transition-colors duration-500 ${
                  p.highlight ? "bg-surface-2" : "bg-navy hover:bg-surface"
                }`}
              >
                {p.highlight && (
                  <>
                    <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-brand-blue to-brand-magenta" />
                    <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand-magenta/20 blur-[80px]" />
                  </>
                )}
                {p.highlight && (
                  <span className="absolute right-0 top-0 bg-brand-magenta px-3 py-1 font-mono text-sm uppercase tracking-[0.2em] text-white">
                    Most popular
                  </span>
                )}
                <h3 className="font-display text-3xl font-medium text-white">{p.name}</h3>
                <p className="mt-2 font-mono text-sm uppercase tracking-[0.15em] text-slate-400">{p.tagline}</p>

                <p className="mt-8 font-display text-5xl font-semibold text-brand-gradient">{p.priceLabel}</p>
                <p className="mt-1 font-mono text-sm uppercase tracking-[0.15em] text-slate-400">
                  Custom quote
                </p>

                <ul className="mt-8 flex-1 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-base text-slate-300">
                      <Check size={16} className="mt-0.5 shrink-0 text-brand-magenta" /> {f}
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/contact?course=${encodeURIComponent(p.name + " plan")}`}
                  data-testid={`pricing-enquire-${p.name.toLowerCase()}`}
                  className={`group mt-10 flex items-center justify-between px-6 py-3.5 font-mono text-sm uppercase tracking-[0.2em] transition-colors duration-300 ${
                    p.highlight
                      ? "bg-brand-blue text-white hover:bg-brand-magenta"
                      : "border border-white/20 text-white hover:border-brand-magenta"
                  }`}
                >
                  Enquire
                  <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div data-testid="pricing-note" className="mt-10 border-l-2 border-brand-magenta bg-surface/40 p-6 sm:p-8">
            <p className="font-mono text-sm uppercase tracking-[0.2em] text-brand-blue">Indicative pricing</p>
            <p className="mt-4 text-lg font-light leading-relaxed text-slate-200">
              Typical cost per delegate is <span className="font-medium text-white">£375 + VAT per training day</span>.
              Exact prices depend on the number of delegates, repeat frequency and any preferred supplier arrangements
              that may be in place.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
